import React, { useEffect, useMemo, useState } from 'react'

import { CollectionContentCardV2 } from '@components/Cards/CollectionContentV2/Card'
import { FeaturedProductCardV2 } from '@components/Cards/ProductV2/Feature'
import { ProductCardV2 } from '@components/Cards/ProductV2/Base'
import { Breadcrumbs } from '@/components/Shared/Breadcrumbs'
import { useCollectionFilters } from '@/hooks/useCollectionFilters'
import type {
  CMSCollectionContentCardV2,
  CMSCollectionGridItem,
  CMSCollectionPage,
  CMSSortBy,
} from '@/types/sanity'
import type { Bottomline } from '@/types/yotpo'
import type { BreadcrumbItem } from '@/utilities/breadcrumbs'
import {
  getAppliedFiltersFromSearchParams,
  getAppliedSortingFromSearchParams,
  isActiveProductCard,
  isCollectionCard,
  sortGridItems,
  updateSearchParams,
} from '@/utilities/collection'

import {
  CollectionContext,
  type CollectionContextType,
} from './collection-context'
import { FilterAndSort } from './FilterAndSort'

import './CollectionPage.scss'
import useMediaQuery from '@/hooks/useMediaQuery'
import config from 'tailwind.config'

type Props = {
  collection: CMSCollectionPage
  activeGridItems?: CMSCollectionGridItem[]
  productBottomlinesMap: Record<string, Bottomline>
  searchParams: Record<string, string>
  breadcrumbs: BreadcrumbItem[]
  sectionAbove?: React.ReactNode
}

export const CollectionPage: React.FC<Props> = ({
  activeGridItems,
  collection: { cardBackgroundColor, cta, filters, sortby, collectionLinks },
  productBottomlinesMap,
  searchParams,
  breadcrumbs,
  sectionAbove,
}) => {
  const [appliedSorting, setAppliedSorting] = useState<CMSSortBy | undefined>(
    getAppliedSortingFromSearchParams({ searchParams, sortby }),
  )

  const {
    appliedFilters,
    setAppliedFilters,
    filteredGridItems,
    isFilterApplied,
    isFilterAvailable,
    handleFilterChange,
  } = useCollectionFilters({
    filters: filters || [],
    gridItems: activeGridItems || [],
    appliedFiltersDefault: getAppliedFiltersFromSearchParams({
      searchParams,
      filters,
    }),
  })

  const contextValue: CollectionContextType = {
    filters: filters || [],
    activeGridItems: activeGridItems || [],
    appliedFilters,
    collectionLinks,
    sortby,
    appliedSorting,
    isFilterApplied,
    isFilterAvailable,
    handleFilterChange,
    setAppliedSorting,
    setAppliedFilters,
  }

  const displayedGridItems = useMemo(() => {
    if (!filteredGridItems) return []
    if (!sortby || !appliedSorting) return filteredGridItems

    return sortGridItems({ gridItems: filteredGridItems, appliedSorting })
  }, [filteredGridItems, appliedSorting])

  const isDesktopScreen = useMediaQuery(
    `(min-width: ${config.theme.screens.md})`,
  )

  useEffect(() => {
    updateSearchParams({ filters, appliedFilters, appliedSorting })
  }, [appliedFilters, appliedSorting])

  return (
    <CollectionContext.Provider value={contextValue}>
      {isDesktopScreen && sectionAbove}
      <div className="collection-filter-and-sort-wrapper">
        <FilterAndSort>
          {!isDesktopScreen && sectionAbove}
          <div className="collection-breadcrumbs">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
        </FilterAndSort>
      </div>
      <article className="collection-grid-v2">
        {(displayedGridItems || []).map(({ item, isFeatured }) => {
          if (isActiveProductCard(item.card) && isFeatured) {
            return (
              <article
                className="collection-grid-v2__item--featured"
                key={item.card._id}
              >
                <FeaturedProductCardV2 content={item.card} />
              </article>
            )
          }

          if (isActiveProductCard(item.card)) {
            return (
              <article className="collection-grid-v2__item" key={item.card._id}>
                <ProductCardV2
                  content={item.card}
                  cta={cta}
                  backgroundColor={cardBackgroundColor}
                  bottomline={productBottomlinesMap[item.card.id]}
                />
              </article>
            )
          }

          if (isCollectionCard(item.card)) {
            return (
              <article
                className="collection-grid-v2__item--featured"
                key={item.card._id}
              >
                <CollectionContentCardV2
                  content={item.card as CMSCollectionContentCardV2}
                />
              </article>
            )
          }
        })}
      </article>
    </CollectionContext.Provider>
  )
}
